import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { FinancialBankAccountSelectDTO } from '@gipi-financial/bank-account/models/dto/bank-account-select.dto';
import { FinancialBank } from '@gipi-financial/bank/models/bank.model';
import { FinancialBankService } from '@gipi-financial/bank/services/bank.service';
import { FinancialClientSelectDTO } from '@gipi-financial/client/models/dto/client-select.dto';
import { FinancialCreditClientType } from '@gipi-financial/credit-client/enums/credit-client-type.enum';
import { FinancialCreditClient } from '@gipi-financial/credit-client/models/credit-client.model';
import { FinancialCheckReceivedCreditClientDTO } from '@gipi-financial/credit-client/models/dto/check-received-credit-client.dto';
import { FinancialCreditClientDTO } from '@gipi-financial/credit-client/models/dto/credit-client.dto';
import { FinancialCreditClientService } from '@gipi-financial/credit-client/services/credit-client.service';
import { FinancialPostingCategorySelectDTO } from '@gipi-financial/posting-category/models/dto/posting-category-select.dto';
import { FinancialPostingCategoryService } from '@gipi-financial/posting-category/services/posting-category.service';
import { CustomMessageService } from '@gipi-shared/services/custom-message.service';
import { APP_MESSAGES, AbstractComponent, ArrayUtil, CurrencyUtil, DateUtil, DocumentUtil, GIPIPageModel, GIPISortModel, INJECTOR, NumberUtil, ObjectUtil, StringUtil, TableColumnBuilder, TableColumnDTO } from '@gipisistemas/ng-core';

class CreditClientCheckReceived {
    cpfOrCnpjIssuer: string;
    issuer: string;
    phone: string;
    bank: FinancialBank;
    agency: string;
    agencyDigit: string;
    account: string;
    accountDigit: string;
    comments: string;
    client: FinancialClientSelectDTO;
    bankAccount: FinancialBankAccountSelectDTO;

    // Check
    checkNumber: number;
    amount: number;
    issuanceDate: Date;
    dueDate: Date;
    postingCategory: FinancialPostingCategorySelectDTO;
}

export interface CreditClientCheckData {
    creditClient: FinancialCreditClientDTO;
    client: FinancialClientSelectDTO;
    postingCategory: FinancialPostingCategorySelectDTO;
    bankAccount: FinancialBankAccountSelectDTO;
}

@Component({
    templateUrl: './credit-client-check-dialog.component.html',
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
    `]
})
export class CreditClientCheckDialogComponent extends AbstractComponent implements OnInit {

    @ViewChild('actions', { static: true }) actions: TemplateRef<any>;

    public tableColumns: TableColumnDTO[] = [];

    public checkReceivedList: CreditClientCheckReceived[] = [];

    public entity: CreditClientCheckReceived = new CreditClientCheckReceived();

    public creditType: FinancialCreditClientType = 'CREDIT';

    public creditClientAmount: number = 0;

    public amountAdded: number = 0;

    public amountRemaining: number = 0;

    bankFindByValueFn = async (value: string, page: number) => {
        const result: GIPIPageModel<FinancialBank> = await this._bankService.findByValue(value, page, 50).toPromise();
        return result;
    };
    bankFn = (obj: FinancialBank) => `${obj.code} - ${obj.name}`;

    postingCategoryFindByValueFn = async (value: string, page: number) => {
        const result: GIPIPageModel<FinancialPostingCategorySelectDTO> = await this._postingCategoryService.findByValue(value, page, 10, new GIPISortModel('fullCode', 'ASC'), 'v1', 'CREDIT', true).toPromise();
        return result;
    };
    postingCategoryFn = (obj: FinancialPostingCategorySelectDTO) => `${obj.fullCode} - ${obj.description}`;

    constructor(
        public dialogRef: MatDialogRef<CreditClientCheckDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreditClientCheckData = {
            creditClient: null,
            client: null,
            postingCategory: null,
            bankAccount: null,
        },
        protected messageService: CustomMessageService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        private _bankService: FinancialBankService,
        private _postingCategoryService: FinancialPostingCategoryService,
        private _creditClientService: FinancialCreditClientService,
    ) {
        super(messageService, router, activatedRoute);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.tableColumns = this.createTableColumns();

        this.creditClientAmount = this.data.creditClient.amount;
        this.creditType = this.data.creditClient.type;

        this.checkReceivedList = [];

        this.newCheckReceived();
    }

    public addCheck(): void {
        if (!this._isValidNewCheckReceived()) {
            return;
        }

        this.checkReceivedList = [...this.checkReceivedList, ObjectUtil.clone(this.entity)];
        this.newCheckReceived();
    }

    public removeCheck(check: CreditClientCheckReceived): void {
        const checkList: CreditClientCheckReceived[] = ArrayUtil.clone(this.checkReceivedList);
        checkList.splice(checkList.indexOf(check), 1);

        this.checkReceivedList = ArrayUtil.clone(checkList);
        this.calculateAmountReceivable();
    }

    public newCheckReceived(): void {
        this.entity = new CreditClientCheckReceived();
        this.entity.checkNumber = null;

        this.entity.postingCategory = !ObjectUtil.isNull(this.data.postingCategory) ? ObjectUtil.clone(this.data.postingCategory) : null;

        this.entity.client = ObjectUtil.clone(this.data.client);
        this.entity.issuer = !ObjectUtil.isNull(this.data.client) ? this.data.client.fantasyName || this.data.client.name : '';

        this.entity.cpfOrCnpjIssuer = !ObjectUtil.isNull(this.data.client) ? this.data.client.cpfOrCnpj : '';

        this.entity.bank = null;

        this.entity.bankAccount = !ObjectUtil.isNull(this.data.bankAccount) ? ObjectUtil.clone(this.data.bankAccount) : null;

        this.entity.issuanceDate = new Date();
        this.entity.dueDate = new Date();
        this.entity.comments = ' ';
        this.calculateAmountReceivable();
    }

    public calculateAmountReceivable(): void {
        this.amountAdded = this.checkReceivedList.reduce((sum, checkReceived) => sum += checkReceived.amount, 0);
        this.amountRemaining = this.creditClientAmount - this.amountAdded;
        this.entity.amount = this.amountRemaining;
    }

    public confirm(): void {
        try {
            if (!this._isValidConfirm()) {
                return;
            }
            this.loading = true;

            const creditClient = ObjectUtil.clone(this.data.creditClient);
            creditClient.checkReceivedList = this._prepareCheckReceivedList();

            this._creditClientService.generateCredit([creditClient]).toPromise().then((creditClientSavedList) => {
                if (!ArrayUtil.isEmpty(creditClientSavedList)) {
                    this.addSuccessMessage(INJECTOR.get(APP_MESSAGES).SUCCESS);
                    this.close(creditClientSavedList[0]);
                }
            }, error => {
                this.loading = false;
                throw new Error(error);
            });
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    private _prepareCheckReceivedList(): FinancialCheckReceivedCreditClientDTO[] {
        if (ArrayUtil.isEmpty(this.checkReceivedList)) {
            return [];
        }

        const checkReceivedList: FinancialCheckReceivedCreditClientDTO[] = [];

        for (const oldCheckReceived of this.checkReceivedList) {
            const checkRecPrepared = new FinancialCheckReceivedCreditClientDTO();
            checkRecPrepared.account = oldCheckReceived.account;
            checkRecPrepared.accountDigit = oldCheckReceived.accountDigit;
            checkRecPrepared.agency = oldCheckReceived.agency;
            checkRecPrepared.agencyDigit = oldCheckReceived.agencyDigit;
            checkRecPrepared.amount = oldCheckReceived.amount;
            checkRecPrepared.bankId = oldCheckReceived.bank.id;
            checkRecPrepared.checkNumber = oldCheckReceived.checkNumber;
            checkRecPrepared.comments = oldCheckReceived.comments;
            checkRecPrepared.cpfOrCnpjIssuer = oldCheckReceived.cpfOrCnpjIssuer;
            checkRecPrepared.dueDate = oldCheckReceived.dueDate;
            checkRecPrepared.issuanceDate = oldCheckReceived.issuanceDate;
            checkRecPrepared.issuerName = oldCheckReceived.issuer;
            checkRecPrepared.phone = oldCheckReceived.phone;
            checkReceivedList.push(checkRecPrepared);
        }

        return checkReceivedList;
    }

    private _isValidConfirm(): boolean {
        const amountAdded: number = Number(this.checkReceivedList.reduce((sum, checkReceived) => sum += checkReceived.amount, 0).toFixed(2));
        const amountReceivable: number = Number((this.creditClientAmount - amountAdded).toFixed(2));

        if (amountAdded < this.creditClientAmount) {
            this.addWarningMessage(`Ainda falta lançar R$ ${CurrencyUtil.transform(amountReceivable, '1.2-2')} para corresponder o valor total`);
            return false;
        }
        if (amountAdded > this.creditClientAmount) {
            this.addWarningMessage(`O valor lançado R$ ${CurrencyUtil.transform(Number((amountAdded).toFixed(2)), '1.2-2')} está ultrapassando o valor total`);
            return false;
        }
        if (ArrayUtil.isEmpty(this.checkReceivedList)) {
            this.addWarningMessage(`É necessário adicionar no mínimo um cheque, para finalizar`);
            return false;
        }

        return true;
    }

    private _isValidNewCheckReceived(): boolean {
        if (StringUtil.isEmpty(this.entity.cpfOrCnpjIssuer.trim())) {
            this.addWarningMessage('Campo CPF/CNPJ é obrigatório e não foi informado');
            return false;
        } else if ((this.entity.cpfOrCnpjIssuer.length !== 11) && (this.entity.cpfOrCnpjIssuer.length !== 14)) {
            this.addWarningMessage('Campo CPF/CNPJ foi informado incorretamente');
            return false;
        } else {
            if ((this.entity.cpfOrCnpjIssuer.length === 11) && !DocumentUtil.isValidCpf(this.entity.cpfOrCnpjIssuer)) {
                this.addWarningMessage('Campo CPF/CNPJ foi informado incorretamente');
                return false;
            }
            if ((this.entity.cpfOrCnpjIssuer.length === 14) && !DocumentUtil.isValidCnpj(this.entity.cpfOrCnpjIssuer)) {
                this.addWarningMessage('Campo CPF/CNPJ foi informado incorretamente');
                return false;
            }
        }
        if (StringUtil.isEmpty(this.entity.issuer)) {
            this.addWarningMessage('Campo emitente é obrigatório e não foi informado');
            return false;
        }
        if (!StringUtil.isEmpty(this.entity.phone) && this.entity.phone.length !== 10) {
            if (!StringUtil.isEmpty(this.entity.phone) && this.entity.phone.length !== 11) {
                this.addWarningMessage('Campo telefone foi informado incorretamente');
                return false;
            }
        }
        if (!NumberUtil.isPositive(this.entity.checkNumber)) {
            this.addWarningMessage('Campo n° cheque é obrigatório e não foi informado');
            return false;
        }
        const amountAdded: number = this.checkReceivedList.reduce((sum, checkReceived) => sum += checkReceived.amount, 0);
        if (amountAdded === this.creditClientAmount) {
            this.addWarningMessage(`O valor lançado R$ ${(amountAdded).toFixed(2)} já corresponde ao valor total`);
            return false;
        }
        if ((this.entity.amount + amountAdded) > this.creditClientAmount) {
            this.addWarningMessage(`O valor que você está tentando lançar R$ ${(amountAdded).toFixed(2)} ultprassa o valor total`);
            return false;
        }
        if (!NumberUtil.isPositive(this.entity.amount)) {
            this.addWarningMessage('Campo valor é obrigatório e não foi informado');
            return false;
        }
        if (!DateUtil.isValid(this.entity.issuanceDate)) {
            this.addWarningMessage('Campo data da emissão é obrigatório e não foi informado');
            return false;
        }
        if (!DateUtil.isValid(this.entity.dueDate)) {
            this.addWarningMessage('Campo data do vencimento é obrigatório e não foi informado');
            return false;
        }
        if (ObjectUtil.isNewModel(this.entity.postingCategory) || ObjectUtil.isNull(this.entity.postingCategory)) {
            this.addWarningMessage('Campo categoria é obrigatório e não foi informado');
            return false;
        }
        if (ObjectUtil.isNewModel(this.entity.bankAccount) || ObjectUtil.isNull(this.entity.bankAccount)) {
            this.addWarningMessage('Campo conta bancária para depósito é obrigatório e não foi informado');
            return false;
        }

        return true;
    }

    public createTableColumns(): TableColumnDTO[] {
        return [
            TableColumnBuilder.instance()
                .property('dueDate')
                .name('Vencimento')
                .value((obj: CreditClientCheckReceived) => DateUtil.format(obj.dueDate, DateUtil.DATE_FORMAT))
                .align('center center')
                .width(10)
                .build(),
            TableColumnBuilder.instance()
                .property('checkNumber')
                .name('N° cheque')
                .value((obj: CreditClientCheckReceived) => obj.checkNumber)
                .align('center center')
                .width(10)
                .build(),
            TableColumnBuilder.instance()
                .property('postingCategory.description')
                .name('Categoria')
                .value((obj: CreditClientCheckReceived) => obj.postingCategory.description)
                .width(15)
                .build(),
            TableColumnBuilder.instance()
                .property('eminent')
                .name('Emitente')
                .value((obj: CreditClientCheckReceived) => obj.issuer)
                .build(),
            TableColumnBuilder.instance()
                .property('bankAccount.description')
                .name('Conta bancária')
                .value((obj: CreditClientCheckReceived) => obj.bankAccount.description)
                .build(),
            TableColumnBuilder.instance()
                .property('amount')
                .name('Valor')
                .value((obj: CreditClientCheckReceived) => CurrencyUtil.transform(obj.amount, '1.2-2'))
                .align('center center')
                .width(10)
                .build(),
            TableColumnBuilder.instance()
                .property('actions')
                .name('Ações')
                .template(this.actions)
                .align('center center')
                .width(7)
                .build(),
        ];
    }

    public close(creditClient: FinancialCreditClient): void {
        this.dialogRef.close(creditClient);
    }

}
